import { gql } from 'graphql-request';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { ParsedUrlQuery } from 'querystring';

import AkqaGraph from '@/components/graph/akqa-graph';
import { NormalizeIndexData } from '@/components/index/helpers';
import { SITE_NAME } from '@/lib/constants';
import { BUILD_SERVER_SIDE_PROPS_GQL_CLIENT } from '@/lib/graphql';

type IndexProps = {
    name: string;
    dailySnapshots: Array<{
        asOfDate: string;
        netAssetValue: string;
        benchmarks: Array<{
            level: string;
            symbol: string;
        }>;
        isBackcalculated: boolean;
    }>;
    symbol: string;
    firstEffectiveAt: string;
    latestSnapshot: Optional<{
        components: Array<{
            value: string;
            instrument: {
                isCash: boolean;
            };
        }>;
    }>;
};

const Index = ({ name, dailySnapshots, symbol, firstEffectiveAt, latestSnapshot }: IndexProps) => {
    let akqaGraph;
    if (dailySnapshots && dailySnapshots.length !== 0) {
        const { normalizedIndexData, symbolList } = NormalizeIndexData(dailySnapshots, symbol);
        akqaGraph = (
            <AkqaGraph
                indexData={normalizedIndexData}
                symbolList={symbolList}
                height={600}
                color="white"
                indexSymbol={symbol}
                firstEffectiveAt={firstEffectiveAt}
                holdingsCount={
                    (latestSnapshot?.components || []).filter(({ instrument: { isCash } }) => !isCash).length
                }
            />
        );
    }

    const titleText = `${name} | ${SITE_NAME}`;

    return (
        <div className="bg-akqa-green">
            <Head>
                <title>{titleText}</title>
            </Head>

            {akqaGraph}
        </div>
    );
};

Index.layout = 'empty';
export default Index;

// static build code is found below
interface Params extends ParsedUrlQuery {
    symbol: string;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { symbol } = context.params as Params;
    const { hideRange } = context.query;

    const query = gql`
        query getIndex($symbol: String!) {
            index(symbol: $symbol) {
                firstEffectiveAt
                name
                dailySnapshots {
                    asOfDate
                    netAssetValue
                    benchmarks {
                        level
                        symbol
                    }
                    isBackcalculated
                }
                symbol
                latestSnapshot {
                    components: componentsIfNotPrivate {
                        value
                        instrument {
                            isCash
                        }
                    }
                }
            }
        }
    `;

    const variables = {
        symbol: symbol.toUpperCase(),
    };

    const client = BUILD_SERVER_SIDE_PROPS_GQL_CLIENT(context.req.headers);
    const response: {
        index: Optional<{
            name: string;
            firstEffectiveAt: string;
            symbol: string;
            dailySnapshots: Array<{
                asOfDate: string;
                netAssetValue: string;
                benchmarks: Array<{
                    level: string;
                    symbol: string;
                }>;
                isBackcalculated: boolean;
            }>;
            latestSnapshot: Optional<{
                components: Array<{
                    value: string;
                }>;
            }>;
        }>;
    } = await client.request(query, variables);
    const data = response.index;

    if (!data) {
        return { notFound: true };
    }

    return {
        props: {
            dailySnapshots: data.dailySnapshots,
            firstEffectiveAt: data.firstEffectiveAt,
            hideRange: hideRange ?? false,
            latestSnapshot: data.latestSnapshot,
            name: data.name,
            symbol: data.symbol,
        },
    };
};
